import { useCallback, useEffect, useMemo, useState } from "react";
import { ComboBox } from "../combo-box";
import type { ComboBoxItem } from "../combo-box/types";
import {
  type Canton,
  type District,
  type Province,
  costaRicaAddressRegistry,
  panamaAddressRegistry,
  filterCantonsByProvince,
  filterDistrictsByCanton,
  getCantonComboBoxValue,
  getProvinceComboBoxValue,
  getDistrictComboBoxValue,
  handleCantonChange as serviceHandleCantonChange,
  handleDistrictChange as serviceHandleDistrictChange,
  handleProvinceChange as serviceHandleProvinceChange,
  cantonsToComboBoxItems,
  districtsToComboBoxItems,
  provincesToComboBoxItems,
  createAddressRegistry,
} from "../../lib/addressPicker/utils/addressPicker";

type AddressRegistryReturn = ReturnType<typeof createAddressRegistry>;

export type AddressPickerValue = {
  address: string;
  province: string | number;
  canton: string | number;
  district: string | number;
};

type Source = "cr" | "pa";

type Props = {
  address: string;
  province?: string | number;
  canton?: string | number;
  district?: string | number;
  disabled?: boolean;
  showAddressInput?: boolean;
  onChange: (v: AddressPickerValue) => void;
  useFixedPosition?: boolean;
  className?: string;
  /** `cr` = Costa Rica (default), `pa` = Panamá */
  source?: Source;
};

const registries: Record<Source, AddressRegistryReturn> = {
  cr: costaRicaAddressRegistry,
  pa: panamaAddressRegistry,
};

const labelsBySource: Record<
  Source,
  { l1: string; l2: string; l3: string; p1: string; p2: string; p3: string }
> = {
  cr: {
    l1: "Provincia",
    l2: "Cantón",
    l3: "Distrito",
    p1: "Seleccione una provincia",
    p2: "Seleccione un cantón",
    p3: "Seleccione un distrito",
  },
  pa: {
    l1: "Provincia",
    l2: "Distrito",
    l3: "Corregimiento",
    p1: "Seleccione una provincia",
    p2: "Seleccione un distrito",
    p3: "Seleccione un corregimiento",
  },
};

/**
 * Provincia + dos niveles administrativos + dirección (texto).
 * `source="cr"` usa datos de Costa Rica; `source="pa"` usa datos de Panamá.
 */
export function AddressPicker({
  address,
  province = 0,
  canton = 0,
  district = 0,
  disabled = false,
  showAddressInput = true,
  onChange,
  useFixedPosition = false,
  className = "",
  source = "cr",
}: Props) {
  const reg = registries[source];
  const lb = labelsBySource[source];

  const provinces = useMemo(() => reg.createProvinces(), [reg]);
  const cantons = useMemo(() => reg.createCantons(), [reg]);
  const districtList = useMemo(() => reg.createDistricts(), [reg]);

  const [selectedProvince, setSelectedProvince] = useState<Province | null>(
    null
  );
  const [selectedCanton, setSelectedCanton] = useState<Canton | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<District | null>(
    null
  );

  useEffect(() => {
    const p = reg.loadProvinceSelection(province, null);
    const c = reg.loadCantonSelection(canton, province, p, null);
    const d = reg.loadDistrictSelection(district, c, null);
    setSelectedProvince(p);
    setSelectedCanton(c);
    setSelectedDistrict(d);
  }, [province, canton, district, reg]);

  const filteredCantons = filterCantonsByProvince(cantons, selectedProvince);
  const filteredDistricts = filterDistrictsByCanton(
    districtList,
    selectedCanton
  );

  const emit = useCallback(
    (next: AddressPickerValue) => {
      onChange(next);
    },
    [onChange]
  );

  /** Evita usar props atrasados: el padre puede aún tener province/canton/district en 0. */
  const emitAddressLine = useCallback(
    (nextAddress: string) => {
      emit({
        address: nextAddress,
        province: selectedProvince
          ? String(selectedProvince.id)
          : province,
        canton: selectedCanton
          ? (selectedCanton.id % 100).toString().padStart(2, "0")
          : canton,
        district: selectedDistrict
          ? (selectedDistrict.id % 100).toString().padStart(2, "0")
          : district,
      });
    },
    [
      emit,
      selectedProvince,
      selectedCanton,
      selectedDistrict,
      province,
      canton,
      district,
    ]
  );

  const onProvinceItem = (item: ComboBoxItem) => {
    const prov = item.value as Province;
    const result = serviceHandleProvinceChange(
      prov,
      selectedCanton,
      selectedDistrict
    );
    setSelectedProvince(result.selectedProvince);
    setSelectedCanton(result.selectedCanton);
    setSelectedDistrict(result.selectedDistrict);
    emit({
      address,
      province: result.province,
      canton: result.canton,
      district: result.district,
    });
  };

  const onCantonItem = (item: ComboBoxItem) => {
    const cant = item.value as Canton;
    const result = serviceHandleCantonChange(cant, selectedDistrict);
    setSelectedCanton(result.selectedCanton);
    setSelectedDistrict(result.selectedDistrict);
    emit({
      address,
      province: String(cant.province),
      canton: result.canton,
      district: result.district,
    });
  };

  const onDistrictItem = (item: ComboBoxItem) => {
    const dist = item.value as District;
    const result = serviceHandleDistrictChange(dist);
    setSelectedDistrict(result.selectedDistrict);
    const cantForEmit = selectedCanton
      ? (selectedCanton.id % 100).toString().padStart(2, "0")
      : canton;
    const provForEmit = selectedProvince
      ? String(selectedProvince.id)
      : province;
    emit({
      address,
      province: provForEmit,
      canton: cantForEmit,
      district: result.district,
    });
  };

  return (
    <div className={`flex flex-col gap-4 ${className}`.trim()}>
      {showAddressInput ? (
        <div className="flex flex-col gap-1">
          <label
            className="text-sm font-medium text-stone-800"
            htmlFor="ap-address"
          >
            Dirección
          </label>
          <input
            id="ap-address"
            name="address"
            type="text"
            className="min-h-10 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-[#2D6B45] focus:ring-1 focus:ring-[#2D6B45] disabled:opacity-60"
            placeholder="Ingrese la dirección específica"
            value={address}
            disabled={disabled}
            onChange={(e) => emitAddressLine(e.target.value)}
          />
        </div>
      ) : null}

      <div className="flex flex-col gap-1">
        <label
          className="text-sm font-medium text-stone-800"
          htmlFor="ap-province"
        >
          {lb.l1}
        </label>
        <div id="ap-province">
          <ComboBox
            placeholder={lb.p1}
            items={
              provincesToComboBoxItems(provinces) as unknown as ComboBoxItem[]
            }
            selectedValue={
              getProvinceComboBoxValue(
                selectedProvince
              ) as unknown as ComboBoxItem | null
            }
            onSelectItem={onProvinceItem}
            showAddNew={false}
            disabled={disabled}
            unlimitedOptions
            useFixedPosition={useFixedPosition}
            hideLabel
            label=""
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label
            className="text-sm font-medium text-stone-800"
            htmlFor="ap-canton"
          >
            {lb.l2}
          </label>
          <div id="ap-canton">
            <ComboBox
              placeholder={lb.p2}
              items={
                cantonsToComboBoxItems(
                  filteredCantons
                ) as unknown as ComboBoxItem[]
              }
              selectedValue={
                getCantonComboBoxValue(
                  selectedCanton
                ) as unknown as ComboBoxItem | null
              }
              onSelectItem={onCantonItem}
              showAddNew={false}
              disabled={disabled || !selectedProvince}
              unlimitedOptions
              useFixedPosition={useFixedPosition}
              hideLabel
              label=""
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label
            className="text-sm font-medium text-stone-800"
            htmlFor="ap-district"
          >
            {lb.l3}
          </label>
          <div id="ap-district">
            <ComboBox
              placeholder={lb.p3}
              items={
                districtsToComboBoxItems(
                  filteredDistricts
                ) as unknown as ComboBoxItem[]
              }
              selectedValue={
                (selectedDistrict
                  ? getDistrictComboBoxValue(selectedDistrict)
                  : null) as unknown as ComboBoxItem | null
              }
              onSelectItem={onDistrictItem}
              showAddNew={false}
              disabled={disabled || !selectedCanton}
              unlimitedOptions
              useFixedPosition={useFixedPosition}
              hideLabel
              label=""
            />
          </div>
        </div>
      </div>
    </div>
  );
}
